import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'users-permissions',
  slug: 'users-permissions',
  name: 'Users & Permissions',
  description: 'Manage staff accounts and assign access through the hierarchical admin.<function>.<scope> grant model, including prebuilt presets and a diff preview before you apply a change.',
  icon: 'ManageAccounts',
  endpoints: [
    {
      slug: 'list-users',
      method: 'GET',
      path: '/api/users',
      summary: 'List staff users',
      description: 'Returns every staff/admin account in the current tenant (public fields only — no password hashes or TOTP secrets). System-managed tenant-switch mirror identities are excluded.',
      auth: 'session',
      responseExample: `[
  {
    "id": "usr_7d21",
    "username": "mrivera",
    "name": "Maria Rivera",
    "role": "staff",
    "email": "mrivera@example.org",
    "ssoOnly": false,
    "totpEnabled": true,
    "readOnly": false,
    "grants": ["admin.technician.tenant", "admin.helpdesk.tenant"],
    "navPinned": [],
    "hasAvatar": true,
    "hasSignature": false
  }
]`,
      sourceFile: 'routes/users.js, lib/auth.js',
    },
    {
      slug: 'create-user',
      method: 'POST',
      path: '/api/users',
      summary: 'Create a staff user',
      description: 'Creates a new account. Set a password, an email (for Google sign-in), or both. Requesting role "admin" is only honored for a caller who already holds full-admin authority; a scoped user-admin can only create staff accounts. If grants are omitted, an admin account defaults to full admin and a staff account defaults to no grants (default-deny).',
      auth: 'session',
      requestBodyExample: `{
  "username": "jklein",
  "name": "Jordan Klein",
  "email": "jklein@example.org",
  "password": "TempPass123",
  "role": "staff",
  "grants": ["admin.helpdesk.tenant"]
}`,
      responseExample: `{
  "id": "usr_9c14",
  "username": "jklein",
  "name": "Jordan Klein",
  "role": "staff",
  "email": "jklein@example.org",
  "ssoOnly": false,
  "totpEnabled": false,
  "readOnly": false,
  "grants": ["admin.helpdesk.tenant"],
  "navPinned": [],
  "hasAvatar": false,
  "hasSignature": false
}`,
      sourceFile: 'routes/users.js',
    },
    {
      slug: 'update-user',
      method: 'PUT',
      path: '/api/users/:id',
      summary: 'Update a staff user',
      description: 'Updates name, email, role, password, or grants for an existing account. A caller can never assign a grant wider than their own effective grants, and (short of full-admin authority) can never create/promote an admin account or modify an existing one. Changing `grants` is audited as a `permission_change` event with a from/to diff.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'User id.' }],
      requestBodyExample: `{
  "name": "Jordan Klein",
  "grants": ["admin.helpdesk.tenant", "admin.technician.tenant"]
}`,
      responseExample: `{
  "id": "usr_9c14",
  "username": "jklein",
  "name": "Jordan Klein",
  "role": "staff",
  "email": "jklein@example.org",
  "ssoOnly": false,
  "totpEnabled": false,
  "readOnly": false,
  "grants": ["admin.helpdesk.tenant", "admin.technician.tenant"],
  "navPinned": [],
  "hasAvatar": false,
  "hasSignature": false
}`,
      sourceFile: 'routes/users.js',
    },
    {
      slug: 'delete-user',
      method: 'DELETE',
      path: '/api/users/:id',
      summary: 'Delete a staff user',
      description: 'Deletes an account. Blocked for the caller\'s own account, for the last remaining admin-role account, and for the last account holding an unrestricted full-admin grant, so a tenant can never be left with zero administrators.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'User id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/users.js',
    },
    {
      slug: 'apply-preset',
      method: 'POST',
      path: '/api/users/:id/apply-preset',
      summary: 'Apply a permission preset to a user',
      description: 'Replaces a user\'s grants with one of the prebuilt named bundles (e.g. "technician", "finance", "readonly"). Subject to the same grant-escalation and last-full-admin guards as a direct grants update, and audited as a `permission_change` event that includes the preset id.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'User id.' }],
      requestBodyExample: `{ "preset": "technician" }`,
      responseExample: `{
  "id": "usr_9c14",
  "username": "jklein",
  "name": "Jordan Klein",
  "role": "staff",
  "grants": ["admin.technician.tenant", "admin.helpdesk.tenant"],
  "ssoOnly": false,
  "totpEnabled": false,
  "readOnly": false,
  "navPinned": [],
  "hasAvatar": false,
  "hasSignature": false
}`,
      sourceFile: 'routes/users.js',
    },
    {
      slug: 'list-permission-presets',
      method: 'GET',
      path: '/api/permissions/presets',
      summary: 'List permission presets',
      description: 'Returns the catalog of prebuilt, named grant bundles (technician, finance, principal, readonly, superadmin) that the permission builder UI applies to users in one step.',
      auth: 'session',
      responseExample: `{
  "presets": [
    { "id": "technician", "label": "Technician", "description": "Helpdesk + device management; no finance or billing", "grants": ["admin.technician.tenant", "admin.helpdesk.tenant"] },
    { "id": "finance", "label": "Finance Officer", "description": "Billing and contracts; read-only on helpdesk", "grants": ["admin.finance.tenant", "admin.readonly.tenant"] },
    { "id": "superadmin", "label": "Super Admin", "description": "Everything, all tenants", "grants": ["admin"] }
  ]
}`,
      sourceFile: 'routes/permissions.js, lib/permissions.js',
    },
    {
      slug: 'list-roles',
      method: 'GET',
      path: '/api/permissions/roles',
      summary: 'List the assignable grant catalog',
      description: 'Returns the full taxonomy of assignable `admin.<function>.<scope>` grant strings (e.g. `admin.technician.tenant`, `admin.finance.global`) with human-readable labels, plus the default grants for a legacy admin account. This is the raw catalog the preset and diff endpoints build on.',
      auth: 'session',
      responseExample: `{
  "roles": [
    { "key": "admin", "label": "Super Admin — everything, all tenants" },
    { "key": "admin.tenant.true", "label": "Tenant Admin — full admin of one tenant" },
    { "key": "admin.user.tenant", "label": "User Admin — manage users, one tenant" },
    { "key": "admin.technician.tenant", "label": "Technician — one tenant" },
    { "key": "admin.finance.tenant", "label": "Finance — one tenant" }
  ],
  "default": ["admin"]
}`,
      sourceFile: 'routes/permissions.js, lib/permissions.js',
    },
    {
      slug: 'preview-grant-diff',
      method: 'POST',
      path: '/api/permissions/diff',
      summary: 'Preview a grant change',
      description: 'Pure preview with no writes: given a `before` grant set (or a `userId` to look one up) and an `after` grant set (or a `preset` id), returns the literal grants added/removed and the catalog capabilities gained/lost. Used by the permission builder to show an admin the effect of a change before it is applied via PUT /api/users/:id or the apply-preset endpoint. Each side is capped at 256 grant entries.',
      auth: 'session',
      requestBodyExample: `{
  "userId": "usr_9c14",
  "preset": "technician"
}`,
      responseExample: `{
  "before": ["admin.helpdesk.tenant"],
  "after": ["admin.technician.tenant", "admin.helpdesk.tenant"],
  "added": ["admin.technician.tenant"],
  "removed": [],
  "unchanged": ["admin.helpdesk.tenant"],
  "capabilities": {
    "gained": ["admin.technician.tenant"],
    "lost": []
  },
  "changed": true
}`,
      sourceFile: 'routes/permissions.js, lib/permissions.js',
    },
  ],
};
